package diana.soleil.design.iraniancanadienbazzar.core.model

import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable

/**
 * What a member of the public is asking us to do about a listing. Kept deliberately simple so the
 * form works without an account (privacy law: owners must be able to correct/remove their info
 * without first becoming a user).
 */
@Serializable
enum class ListingRequestType {
    /** "Take my listing down / I don't want to be listed." */
    @SerialName("removal") Removal,

    /** "Some information here is wrong." */
    @SerialName("correction") Correction,

    /** "This exposes private info (e.g. my home address)." */
    @SerialName("privacy") Privacy,
}

/** Admin lifecycle of a [ListingRequest] (`listingRequests/{id}.status`). */
@Serializable
enum class ListingRequestStatus {
    @SerialName("open") Open,
    @SerialName("resolved") Resolved,
}

/**
 * A no-login request from the public to **remove or correct a directory listing** — the privacy
 * "takedown / correction" path. Stored as `listingRequests/{id}` and written even by unauthenticated
 * visitors (strict shape, admin-read-only — the `waitlist` precedent), because the person who wants
 * their info removed is usually not a signed-in user. Admins action it from the moderation queue.
 *
 * [businessName] is denormalized so the admin queue renders without a second read. Timestamp is epoch
 * millis (or null while a server timestamp resolves), keeping this model free of any Firestore type.
 */
@Serializable
data class ListingRequest(
    val id: String,
    val businessId: String,
    val businessName: String,
    val type: ListingRequestType = ListingRequestType.Correction,
    /** Free text: what to fix / why to remove. */
    val message: String,
    /** Optional reply-to address so we can follow up; may be blank for anonymous requests. */
    val contactEmail: String? = null,
    val status: ListingRequestStatus = ListingRequestStatus.Open,
    val createdAtMillis: Long? = null,
)
